import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux'
import dateFormat from 'dateformat'

import SaveButton from '../components/psc/lib/SaveButton'
import ErrorMessage from '../components/psc/lib/ErrorMessage'
import CancelButton from '../components/psc/lib/CancelButton'
import DeleteButton from '../components/psc/lib/DeleteButton'
import { PscPhrase } from '../components/psc/psc-phrase';
import FilePaths, { FileButtons } from '../components/psc/lib/FilePaths'
import UserSelect from '../components/psc/lib/UserSelect'
import SingleSelect from '../components/psc/lib/SingleSelect'
import HasTemplate from '../components/psc/lib/HasTemplate'
import TextArea from '../components/psc/lib/TextArea'
import PickDate from '../components/psc/lib/PickDate'
import Title from '../components/psc/lib/Title'
import DocumentVersion from '../components/psc/lib/DocumentVersion'
import Applicability from '../components/psc/lib/Applicability'
import Classification from '../components/psc/lib/Classification'
import HeaderStatusInfo from '../components/psc/lib/HeaderStatusInfo'
import Status from '../components/psc/lib/Status'
import RelatedItems from '../components/psc/lib/RelatedItems'
import CustomFields from '../components/psc/lib/CustomFields'
import Loading from '../components/psc/lib/Loading'
import NoAccess from '../components/psc/lib/NoAccess'
import DocumentTraceability from '../components/psc/lib/DocumentTraceability'

import useAuthentication from '../hooks/useAuthentication';
import useCadmObject from '../hooks/useCadmObject'
import useDocumentStatuses from '../hooks/useDocumentStatuses'
import useUserManagement from '../hooks/useUserManagement'
import useProjectContents from '../hooks/useProjectContents'
import useGncHistory from '../hooks/useGncHistory'
import useConfiguration from '../hooks/useConfiguration'

const Document = () => {
    const pscState = useSelector(state => state.pscState.value)
    const project = pscState.project
    const { featureSharepoint, featureTraceability, documentsInitialIssue } = useConfiguration()
    const navigate = useNavigate()
    const location = useLocation()
    const params = useParams();
    const isNew = params.objID === 'new';
    const projectID = Number(params.projectID);
    const objID = Number(params.objID);
    
    const { currentUser } = useAuthentication();

    const {
        getUserFromID,
        userHasRole,
        userHasExplicitProjectAccess,
        userHasProjectAccessLevel,
    } = useUserManagement()

    const {
        initCustomFields,
        referenceStructure,
        stdDocumentMap,
        getStdDocument,
        getUnarchivedStdDocuments,
    } = useCadmObject('documents')

    const {
        documentStatuses,
    } = useDocumentStatuses()

    const {
        updateDocument,
        deleteDocument,
        forceReloadDocuments,
    } = useProjectContents()

    const {
        goBack,
        replaceCurrentPath,
    } = useGncHistory()
    
    // Document fields
    const [ id, setID ] = useState(null)
    const [ customFields, setCustomFields ] = useState([])
    const [ documentStatusID, setDocumentStatusID ] = useState(1)
    const [ originalDocumentStatusID, setOriginalDocumentStatusID ] = useState(1)
    const [ reference, setReference ] = useState('')
    const [ referenceWithoutVersion, setReferenceWithoutVersion ] = useState('')
    const [ title, setTitle ] = useState('')
    const [ note, setNote ] = useState('')
    const [ stdDocumentID, setStdDocumentID ] = useState(null)
    const [ finalVersionFilePath, setFinalVersionFilePath ] = useState('')
    const [ filePaths, setFilePaths ] = useState([])
    const [ ownerUserID, setOwnerUserID ] = useState(currentUser.id)
    const [ issue, setIssue ] = useState(null)
    const [ revision, setRevision ] = useState(null)
    const [ issueDate, setIssueDate ] = useState(null)
    const [ documentModels, setDocumentModels ] = useState([])
    const [ classification, setClassification ] = useState({ id: null, name: '', description: '' }) // For smart templates, populate fields with "" if there's no classification
    const [ hasClassificationOptions, setHasClassificationOptions ] = useState(true)
    
    // Read-only
    const [ characteristics, setCharacteristics ] = useState({})
    const [ incorporatedInProjects, setIncorporatedInProjects ] = useState(null)
    const [ isUsed, setIsUsed ] = useState(null)
    const [ forkedFrom, setForkedFrom ] = useState(null)
    const [ forkedFromIncDocID, setForkedFromIncDocID ] = useState(null)
    const [ forkedFromID, setForkedFromID ] = useState(null)
    const [ updatedAt, setUpdatedAt ] = useState(null)
    
    const [ relatedItems, setRelatedItems ] = useState([])
    const [ previousVersionRelatedItems, setPreviousVersionRelatedItems ] = useState([])
    
    // Document specific read-only
    const [ deliveryLists, setDeliveryLists ] = useState(null)
    
    // Form controls
    const [ downloadOnSave, setDownloadOnSave ] = useState(true)
    const [ error, setError ] = useState(false)
    
    const [ submitActive, setSubmitActive ] = useState(false)
    const [ submitSpinner, setSubmitSpinner ] = useState(false)
    const [ upissueSpinner, setUpissueSpinner ] = useState(false)
    const [ uprevisionSpinner, setUprevisionSpinner ] = useState(false)
    const [ showTemplateModal, setShowTemplateModal ] = useState(false)
    const [ hasLoaded, setHasLoaded] = useState(isNew ? true: false)
    const [ hasAccess, setHasAccess ] = useState(true)
    
    const documentStatusName = useMemo(() => {
        const status = documentStatuses.find((element) => element.id === documentStatusID)
        return status ? status.name : ""
    }, [documentStatuses, documentStatusID])
    
    const originalDocumentStatusName = useMemo(() => {
        const status = documentStatuses.find((element) => element.id === originalDocumentStatusID)
        return status ? status.name : ""
    }, [documentStatuses, originalDocumentStatusID])
    
    const hasIssue = useMemo(() => {
        return referenceStructure?.match(/\$Issue/) === null ? false : true
    }, [referenceStructure])

    const hasRevision = useMemo(() => {
        return referenceStructure?.match(/\$Revision/) === null ? false : true
    }, [referenceStructure])

    const defaultIssue = useMemo(() => {
        if(hasIssue) {
            return documentsInitialIssue
        }
        return null
    }, [hasIssue])

    const defaultRevision = useMemo(() => {
        // Special case, for Reference only structures, we default to A
        if(hasIssue || !hasRevision) {
            return ""
        }
        return "A"
    }, [hasIssue, hasRevision])

    const stdDocument = useMemo(() => {
        return getStdDocument(stdDocumentID)
    }, [stdDocumentMap, stdDocumentID]);
    
    useEffect(() => {
        if (featureSharepoint && filePaths.length === 0) {
            setFilePaths(['']);
        }
    }, [featureSharepoint]);

    const filePathsInUse = useMemo(() => {
        if (featureSharepoint) {
            return true;
        }
        return documentStatusName === "Superseded" || documentStatusName === "Live";
    }, [featureSharepoint, documentStatusName]);
    
    useEffect(() => {
        if( isNew ) {
            return
        }
        fetch('/v0/delivery_lists?delivery_list_status_id=2&document_id=' + objID + '&project_id=' + projectID, { credentials: 'same-origin' })
            .then(function(response) {
                if (!response.ok) throw response
                return response.json()
            })
            .then(function(json) {
                setDeliveryLists(json.results)
            })
            .catch(error => console.log("Document Get Delivery Lists error", error))
    }, [objID, projectID, isNew])

    useEffect(() => {
        if( ! isNew || ! initCustomFields ) {
            return
        }
        setCustomFields(initCustomFields)
    }, [initCustomFields])
    
    useEffect(() => {
        if (isNew) {
            setOwnerUserID(currentUser.id)
            setIssue(defaultIssue)
            setRevision(defaultRevision)

            // Used when forking (included) document
            const location_state = location.state
            if( location_state !== null ) {
                setForkedFromIncDocID(location.state.forked_from_inc_doc_id)
                setForkedFromID(location.state.forked_from_id)
                setForkedFrom(location.state.forked_from)
                setStdDocumentID(location_state.std_document_id)
                setTitle(location_state.title)
                setClassification(location_state.classification)
                setNote(location_state.note)
                setDocumentModels(location_state.document_models)
            }
            return
        }
        
        fetchObject()
    }, [objID, isNew, defaultIssue, defaultRevision])

    const fieldsDisabled = useMemo(() => {
        if (!isNew && originalDocumentStatusName === "Live") {
            return true
        }

        if (originalDocumentStatusName === "Withdrawn" || originalDocumentStatusName === "Superseded") {
            return true
        }

        if (!userHasProjectAccessLevel(currentUser,projectID, "MEMBER")) {
            return true
        }
        return false
    }, [isNew, originalDocumentStatusName, currentUser])

    useEffect(() => {
        if( isNew ) {
            getReference()
        }
    }, [isNew, stdDocumentID])


    // Convert object to lowercase json
    const toBackend = () => {
        let data = {
            project_id: projectID,
            std_document_id: stdDocumentID,
            reference: reference,
            revision: revision,
            issue: issue,
            title: title,
            classification_id: classification.id,
            classification: classification,
            note: note,
            custom_fields: customFields,
            owner_user_id: ownerUserID,
            document_models: documentModels,
            issue_date: issueDate,
            document_status_id: documentStatusID,
            final_version_file_path: finalVersionFilePath,
            file_paths: filePaths,
            related_items: relatedItems,
            forked_from_inc_doc_id: forkedFromIncDocID,
            forked_from_id: forkedFromID,
            forked_from: forkedFrom,
            updated_at: updatedAt
        }

        if( !isNew ) {
            data.id = id
        }
        
        // Hack hack hack.. If status == Reserved or Withdrawn, clear out all file paths
        const status = documentStatusName
        if( status === "Reserved" || status === "Withdrawn" ) {
            data.final_version_file_path = ''
            data.file_paths = []
        }
        return data
    }
    
    const fetchObject = () => {
        fetch('/v0/documents/' + objID, { credentials: 'same-origin' })
            .then(function(response) {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error(response.statusText)
                }
            })
            .then(function(json) {
                if(json.error) {
                    throw new Error(json.error)
                }

                const obj = json.result
                setID(obj.id)
                setReference(obj.reference)
                setCustomFields(obj.custom_fields)
                setDocumentStatusID( obj.document_status_id )
                setTitle(obj.title)
                setNote(obj.note)
                setStdDocumentID(obj.std_document_id)
                setFinalVersionFilePath(obj.final_version_file_path)
                setFilePaths(obj.file_paths)
                setOwnerUserID(obj.owner_user_id)
                setIssueDate(obj.issue_date)
                setDocumentModels(obj.document_models)
                
                setForkedFromIncDocID(obj.forked_from_inc_doc_id)
                setForkedFromID(obj.forked_from_id)
                setForkedFrom(obj.forked_from)
                
                setCharacteristics(obj.characteristics)
                setIsUsed(obj.is_used)
                setRelatedItems(obj.related_items)
                setPreviousVersionRelatedItems(obj.previous_version_related_items)
                setOriginalDocumentStatusID( obj.document_status_id )
                setClassification(obj.classification)
                setReferenceWithoutVersion(obj.reference_without_version)
                setIncorporatedInProjects(obj.incorporated_in_projects)
                setIssue(obj.issue)
                setRevision(obj.revision)
                setHasLoaded(true)
                setUpdatedAt(obj.updated_at)
                if( obj.issue !== defaultIssue || obj.revision !== defaultRevision ) {
                    setDownloadOnSave(false)
                }
            })
            .catch(err => {
                console.log("Document fetch error", err)
                setHasAccess(false)
            })
        
    }

    const getReference = () => {
        if( typeof stdDocumentID === 'undefined' || stdDocumentID === null || stdDocumentID === 0 ) {
            return
        }
        fetch('/v0/documents/reference', {
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            },
            method: 'POST',
            body: JSON.stringify({
                project_id: parseInt(projectID, 10),
                std_document_id: stdDocumentID,
                issue: issue,
                revision: revision
            })
        }).then(function(response) {
            if (!response.ok) throw response
            return response.json()
        }).then(function(json) {
            setReference(json.result.reference)
            setReferenceWithoutVersion(json.result.reference_without_version)
        })
          .catch(error => console.log("Document getReference error", error))
        return;
    }

    const validateBeforeSubmit = () => {
        const status = documentStatusName
        let errors = []

        if ( ! stdDocumentID ) {
            errors.push("Type is required")
        }

        if ( ! title || /^\s+$/.test(title) ) {
            errors.push("Title is required")
        }

        if ( hasClassificationOptions && (!classification.id || classification.is_archived === true) ) {
            errors.push("Classification is required")
        }
        
        if ( ! ownerUserID ) {
            errors.push("Owner is required")
        }
        
        if ( status === "Live" ) {
            if ( ! issueDate ) {
                errors.push("Issue date is required")
            }
            if ( finalVersionFilePath === "" ) {
                errors.push("File Location is required")
            }
        }

        if ( errors.length > 0 ) {
            setError(errors)
            return false
        }
        return true
    }

    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setSubmitSpinner(false)
        setError([ props.message ])
    }
    
    const handleSubmit = (event) => {
        if( event ) {
            event.preventDefault()
        }
        if( submitActive || !validateBeforeSubmit() ) {
            return null
        }

        setSubmitActive(true)
        setSubmitSpinner(true)

        let submit = {
            objType: 'documents',
            objID: objID,
            data: toBackend(),
            successCallback: function(json) {
                updateDocument(json.result)
                forceReloadDocuments()
                setSubmitActive(false)
            }
        }
        
        // Only if still status = Reserved, and there is a template
        if( documentStatusName === "Reserved" && downloadOnSave && stdDocument.template_id > 0 ) {
            if (project.default_sharepoint_folder?.id > 0) {
                // Sharepoint upload, don't do modal, go to different page
                // this modal is in TemplateModal component. open it after save
                submit.successCallback = function(json) {
                    updateDocument(json.result)
                    forceReloadDocuments()
                    setSubmitActive(false)
                }
                submit.navigateOverride = function(json) {
                    navigate('/portfolio/'+projectID+'/documents/'+json.result.id+'/sharepoint_upload')
                }
                
            }
            else {
                // this modal is in TemplateModal component. open it after save
                submit.successCallback = function(json) {
                    updateDocument(json.result)
                    forceReloadDocuments()
                    setShowTemplateModal(true)
                    setSubmitActive(false)
                }
                submit.navigateOverride = function(json) {
                    // Do nothing
                }
            }
        }
        return submit
    }

    const onTemplateModalClose = () => {
        goBack()
    }
    
    const handleSubmitUpissue = () => {
        if( submitActive ) {
            return null
        }

        setSubmitActive(true)
        setUpissueSpinner(true)

        return {
            objType: 'documents',
            objID: objID,
            url: '/v0/documents/'+objID+'/upissue',
            data: toBackend(),
            successCallback: function(json) {
                forceReloadDocuments()
                replaceCurrentPath('/portfolio/'+projectID+'/documents')
                setSubmitActive(false)
            },
            navigateOverride: function(json) {
                navigate('/portfolio/'+projectID+'/documents/'+json.result.id)
            }
        }
    }

    const handleSubmitUprevision = () => {
        if( submitActive ) {
            return null
        }

        setSubmitActive(true)
        setUprevisionSpinner(true)

        return {
            objType: 'documents',
            objID: objID,
            url: '/v0/documents/'+objID+'/uprevision',
            data: toBackend(),
            successCallback: function(json) {
                forceReloadDocuments()
                replaceCurrentPath('/portfolio/'+projectID+'/documents')
                setSubmitActive(false)
            },
            navigateOverride: function(json) {
                navigate('/portfolio/'+projectID+'/documents/'+json.result.id)
            }
        }
    }

    const statusIsDisabled = (status) => {
        if ( ! userHasProjectAccessLevel(currentUser,projectID, "CADM_CONTROLLER") ) {
            return true
        }

        if ( status.name === 'Withdrawn' && isNew ) {
            return true
        }

        let hasLaterNotWithdrawn = characteristics.has_later_not_withdrawn
        let hasLaterLiveOrSuperseded = characteristics.has_later_live_or_superseded

        // As per PSC_Status_Logic_v4.docx

        if ( hasLaterNotWithdrawn ) {
            if ( hasLaterLiveOrSuperseded ) {
                if ( status.name === 'Withdrawn' || status.name === 'Superseded' ) {
                    return false
                }
                else {
                    return true
                }
            }
            else {
                if ( status.name === 'Withdrawn' || status.name === 'Live' ) {
                    return false
                }
                else {
                    return true
                }
            }
        }
        else {
            if ( status.name === 'Withdrawn' || status.name === 'Live' || status.name === 'Reserved' ) {
                return false
            }
            return true
        }
    }

    const notesDisabled = () => {
        if ( ! userHasProjectAccessLevel(currentUser,projectID, "MEMBER") ) {
            return true
        }
        return false
    }

    const templateData = () => {
        const data = {
            classification: classification,
            issue: issue,
            issue_date: issueDate,
            note: note,
            owner_user_id: ownerUserID,
            reference: reference,
            reference_without_version: referenceWithoutVersion,
            revision: revision,
            title: title,

            project: project,
            std_document: { name: stdDocument.name },
        }
        return data
    }

    if (! project || ! projectID > 0 ) {
        return <h1>Loading...</h1>
    }

    const status = documentStatusName
    const originalStatus = originalDocumentStatusName

    let latestNotWithdrawnVersion = true
    let hasLaterNotWithdrawn = characteristics.has_later_not_withdrawn
    if ( hasLaterNotWithdrawn ) {
        latestNotWithdrawnVersion = false
    }

    let upissueButton = '';
    if (!isNew && latestNotWithdrawnVersion
        && hasIssue
        && (originalStatus === "Live")
        && (status === "Live")
        && userHasProjectAccessLevel(currentUser,projectID, "MEMBER")) {

        upissueButton = (
            <SaveButton
                buttonVariant='info'
                visible={true}
                spinnerActive={upissueSpinner}
                label='Upissue'
                onClick={handleSubmitUpissue}
                onError={handleSubmitError}
            />
        )
    }

    let uprevisionButton = '';
    if (!isNew
        && hasRevision
        && latestNotWithdrawnVersion
        && (originalStatus === "Live")
        && (status === "Live")
        && userHasProjectAccessLevel(currentUser,projectID, "MEMBER")) {
        uprevisionButton = (
            <SaveButton
                buttonVariant='warning'
                visible={true}
                spinnerActive={uprevisionSpinner}
                label='Uprevision'
                onClick={handleSubmitUprevision}
                onError={handleSubmitError}
            />
        )
    }

    let deleteButton = []
    if ( !isNew && userHasProjectAccessLevel(currentUser,projectID, "CADM_CONTROLLER") && ! isUsed ) {
        const url = '/v0/documents/'+objID;
        deleteButton.push(
            <DeleteButton
                url={url}
                onError={handleSubmitError}
                callback={() => deleteDocument(objID)}
                extraText="Deleting this item will remove it completely from GNC Go (as though it was never created) - except for existing Tracking Report Baselines. To keep traceability you can Withdraw it instead."
            />
        )
    }

    let indicateRequired = false;

    let requiredForReservedIndicator = '';
    if ((status === "Reserved")
        && userHasProjectAccessLevel(currentUser,projectID, "MEMBER")) {
        requiredForReservedIndicator = !fieldsDisabled && (<label className="form-required-star">&nbsp;*</label>)
        indicateRequired = false
    }

    let requiredForLiveIndicator = '';
    if ((status === "Live")
        && userHasProjectAccessLevel(currentUser,projectID, "CADM_CONTROLLER")) {
        requiredForLiveIndicator = !fieldsDisabled && (<label className="form-required-star">&nbsp;*</label>)
        indicateRequired = true
    }
    
    let forkedFromText = ''
    if (forkedFrom) {
        forkedFromText =
            (<div key="forkedText1" style={{color: "rgba(103, 106, 108, 0.65)"}}>
                <i className="fa fa-code-fork" aria-hidden="true"></i> &nbsp;
                This Document was Forked from: &nbsp;
                <Link to={`/portfolio/${projectID}/included_documents/${forkedFromIncDocID}`}>
                    {forkedFrom.document_reference} {forkedFrom.document_title}
                </Link>
            </div>)
    }
    
    const applicabilityDisabled = userHasProjectAccessLevel(currentUser,projectID, "CADM_CONTROLLER")
                                ? false
                                : fieldsDisabled
    
    // Need to refresh "status is disabled" data -- it depends on data from the backend
    documentStatuses.forEach((element) => element.disabled = statusIsDisabled(element))

    if (!hasAccess) {
        return <NoAccess />
    }
    
    if (!hasLoaded) {
        return <Loading />
    }

    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            
                            <HeaderStatusInfo
                                type="Document"
                                id={id}
                                status={originalStatus}
                            />

                            {forkedFromText}

                            <Title
                                type="Document"
                                reference={reference}
                                title={title}
                                id={id}
                                showLatest={true}
                            />

                            <FileButtons
                                finalVersionFilePath={finalVersionFilePath}
                                buttonClass={originalStatus === "Superseded" ? "btn-warning" : ""}
                                visible={originalStatus === "Superseded" || originalStatus === "Live"}
                            />
                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={(e) => handleSubmit(e)}>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase("LABEL_STD_DOCUMENT")}
                                        { isNew && requiredForReservedIndicator}
                                        { isNew && requiredForLiveIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        { !isNew &&
                                          <input className="form-control" type="text" disabled={true}
                                                 value={stdDocument?.name} />
                                          ||
                                          <SingleSelect
                                              value={stdDocumentID}
                                              onChange={(value) => setStdDocumentID(value.std_document_id)}
                                              options={getUnarchivedStdDocuments()}
                                              required
                                          />
                                        }
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase("LABEL_REFERENCE")}</label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" name="reference"
                                               value={reference}
                                               disabled={true}
                                        />
                                        <span className="help-block m-b-none m-t-none">
                                            <small className="form-info-text" > {PscPhrase("DOCUMENT_CREATED_AUTOMATICALLY")}</small>
                                        </span>
                                    </div>
                                </div>

                                <DocumentVersion
                                    canIssue={hasIssue}
                                    canRevision={hasRevision}
                                    issue={issue}
                                    revision={revision}
                                />
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase("LABEL_TITLE")}
                                        {requiredForReservedIndicator}{requiredForLiveIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" name="title" value={title} onChange={e => setTitle(e.target.value)} disabled={fieldsDisabled} required />
                                    </div>
                                </div>

                                {(hasClassificationOptions || fieldsDisabled) ?
                                    <Classification
                                        isNew={isNew}
                                        value={classification.id}
                                        setClassification={setClassification}
                                        setUseClassification={setHasClassificationOptions}
                                        disabled={fieldsDisabled}
                                        required
                                    />
                                : null }
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase("DOCUMENT_LABEL_NOTE")}</label>
                                    <div className="col-sm-8">
                                        <TextArea className="form-control" value={note} onChange={e => setNote(e.target.value)} disabled={notesDisabled()} />
                                        <span className="help-block m-b-none m-t-none" >
                                            <small className="form-info-text" > {PscPhrase("DOCUMENT_INFO_NOTE")}</small>
                                        </span>

                                    </div>
                                </div>

                                <CustomFields
                                    customFields={customFields}
                                    setCustomFields={setCustomFields}
                                    cadmObjectType='documents'
                                    disabled={notesDisabled()}
                                />

                                <UserSelect
                                    label={PscPhrase("DOCUMENT_LABEL_OWNER")}
                                    required={true}
                                    value={ownerUserID}
                                    onChange={(value) => setOwnerUserID(value)}
                                    disabled={fieldsDisabled}
                                    projectId={projectID}
                                />

                                <div className="hr-line-dashed"></div>

                                <Applicability
                                    projectId={projectID}
                                    projectType={project.project_type.name}
                                    options={project.project_systems}
                                    value={documentModels}
                                    onChange={(value) => setDocumentModels(value)}
                                    disabled={applicabilityDisabled}
                                    onlyAllowOneSystem={featureSharepoint}
                                />
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase("DOCUMENT_LABEL_ISSUE_DATE")}
                                        {requiredForLiveIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <PickDate
                                            value={issueDate}
                                            onChange={setIssueDate}
                                            disabled={fieldsDisabled}
                                        />
                                    </div>
                                </div>

                                <Status
                                    value={documentStatusID}
                                    onChange={(value) => setDocumentStatusID(value) }
                                    statuses={documentStatuses}
                                />

                                <div className="hr-line-dashed"></div>

                                <FilePaths
                                    disabled={!userHasProjectAccessLevel(currentUser,projectID, "CADM_CONTROLLER")}
                                    filePaths={filePaths}
                                    finalVersionFilePath={finalVersionFilePath}
                                    setFilePaths={(value) => setFilePaths(value)}
                                    setFinalVersionFilePath={(value) => setFinalVersionFilePath(value)}
                                    inUse={filePathsInUse}
                                    showFileLabels
                                    showRequired={indicateRequired}
                                    buttonClass={originalStatus === "Superseded" ? "btn-warning" : ""}
                                    displayOriginal
                                />

                                <div className="hr-line-dashed"></div>

                                <RelatedItems
                                    object_type="documents"
                                    object_reference_without_version={referenceWithoutVersion}
                                    project_id={projectID}
                                    project_type={project.project_type.name}
                                    related_items={relatedItems}
                                    previous_version_related_items={previousVersionRelatedItems}
                                    disabled={ ! userHasProjectAccessLevel(currentUser,projectID, "MEMBER") }
                                    onChange={(value) => setRelatedItems(value)}
                                />
                                
                                <div className="hr-line-dashed"></div>

                                <HasTemplate
                                    templateData={templateData}
                                    templateID={stdDocument.template_id}
                                    filename={reference + " " + title}
                                    disabled={! userHasProjectAccessLevel(currentUser,projectID, "MEMBER")}
                                    visible={status === "Reserved" ? true : false}
                                    downloadOnSave={downloadOnSave}
                                    onDownloadOnSaveChange={(value) => setDownloadOnSave(value)}
                                    showModal={showTemplateModal}
                                    onModalClose={onTemplateModalClose}
                                />

                                <div className="hr-line-dashed"></div>

                                { deliveryLists &&
                                  <div className="form-group">
                                      <label className="col-sm-2 control-label"></label>
                                      <div className="col-sm-8">
                                          <label className="control-label">This Document was in the following Deliveries</label>

                                          <ul className="list-group clear-list m-t">
                                              {deliveryLists.length === 0 &&
                                               <li className="list-group-item">
                                                   No Deliveries
                                               </li>
                                              }
                                              {deliveryLists.length > 0 && deliveryLists.map((item) => (
                                                   <li key={item.id} className="list-group-item">
                                                       <Link to={`/portfolio/${item.project_id}/delivery_lists/${item.id}`}>
                                                           {dateFormat(item.sent_date, "dd-mm-yyyy")} - {item.title}
                                                       </Link>
                                                   </li>
                                              ))}
                                          </ul>
                                      </div>
                                  </div>
                                }

            <div className="hr-line-dashed"></div>

            { !featureTraceability && incorporatedInProjects &&
              <div className="form-group">
                  <label className="col-sm-2 control-label"></label>
                  <div className="col-sm-8">
                      <label className="control-label">This Document is Included in the following Projects or Products</label>

                      <ul className="list-group clear-list m-t">
                          {incorporatedInProjects.length === 0 &&
                           <li className="list-group-item">
                               No Projects or Products
                           </li>
                          }
                          {incorporatedInProjects.map((item) => 
                               userHasExplicitProjectAccess(currentUser, item.id )
                                    ? (
                                        <li key={item.id} className="list-group-item">
                                            <Link to={`/portfolio/${item.id}/dashboard`}>
                                                {item.project_code} {item.name}
                                            </Link>
                                        </li>
                                    )
                                    : (
                                        <li key={item.id} className="list-group-item">
                                            {item.project_code} {item.name}
                                        </li>
                                    )
                           )}
                      </ul>
                  </div>
              </div>
            }

            { featureTraceability &&
            <DocumentTraceability
                id={id}
                isNew={isNew}
                projectID={projectID}
                projectName={project.name}
                reference={reference}
                title={title}
                documentStatusID={documentStatusID}
            />
            }
                                
            <div className="form-group">
                <ErrorMessage
                    error={error}
                />

                <div className="col-sm-10 col-sm-offset-2">
                    <CancelButton/>

                    <SaveButton
                        buttonVariant='primary'
                        visible={userHasProjectAccessLevel(currentUser,projectID, "MEMBER")}
                        spinnerActive={submitSpinner}
                        label='Save Changes'
                        onClick={handleSubmit}
                        onError={handleSubmitError}
                    />

                    {deleteButton}

                    {upissueButton}

                    {uprevisionButton}

                </div>
            </div>
            
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default Document
